/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Contacts API Operations
 * 
 * This module handles contacts-related API operations including:
 * - Contact organizations CRUD
 * - Contact teams CRUD
 * - Individual contacts CRUD
 * - Search and filtering
 * - Import operations (CSV, Google)
 */

import { getProductServerBaseUrl, buildProductApiUrl } from './base';
import type {
  ContactOrganization,
  ContactTeam,
  Contact,
  ContactWithDetails,
  ContactOrganizationWithDetails,
  InsertContactOrganization,
  InsertContactTeam,
  InsertContact,
} from '@shared/schema';

// ============================================================================
// Types
// ============================================================================

export interface ContactsApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================================================
// Contact Organizations
// ============================================================================

export async function getContactOrganizations(
  productId?: string
): Promise<ContactsApiResult<ContactOrganization[]>> {
  try {
    const params: Record<string, string> = {};
    if (productId) params.productId = productId;
    
    const apiUrl = buildProductApiUrl('/api/contacts/organizations', params);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch organizations (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching organizations:', error);
    return { success: false, error: error.message };
  }
}

export async function getContactOrganization(
  id: string,
  withDetails?: boolean
): Promise<ContactsApiResult<ContactOrganization | ContactOrganizationWithDetails>> {
  try {
    const params: Record<string, string> = {};
    if (withDetails) params.withDetails = 'true';
    
    const apiUrl = buildProductApiUrl(`/api/contacts/organizations/${id}`, params);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch organization (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching organization:', error);
    return { success: false, error: error.message };
  }
}

export async function createContactOrganization(
  data: InsertContactOrganization
): Promise<ContactsApiResult<ContactOrganization>> {
  try {
    const apiUrl = buildProductApiUrl('/api/contacts/organizations');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to create organization (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error creating organization:', error);
    return { success: false, error: error.message };
  }
}

export async function updateContactOrganization(
  id: string,
  data: Partial<InsertContactOrganization>
): Promise<ContactsApiResult<ContactOrganization>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/organizations/${id}`);
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to update organization (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error updating organization:', error);
    return { success: false, error: error.message };
  }
}

export async function deleteContactOrganization(
  id: string
): Promise<ContactsApiResult<{ success: boolean }>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/organizations/${id}`);
    const response = await fetch(apiUrl, { method: 'DELETE' });
    
    if (!response.ok) {
      return { success: false, error: `Failed to delete organization (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error deleting organization:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Contact Teams
// ============================================================================

export async function getContactTeams(
  organizationId?: string
): Promise<ContactsApiResult<ContactTeam[]>> {
  try {
    const params: Record<string, string> = {};
    if (organizationId) params.organizationId = organizationId;
    
    const apiUrl = buildProductApiUrl('/api/contacts/teams', params);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch teams (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching teams:', error);
    return { success: false, error: error.message };
  }
}

export async function getContactTeam(id: string): Promise<ContactsApiResult<ContactTeam>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/teams/${id}`);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch team (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching team:', error);
    return { success: false, error: error.message };
  }
}

export async function createContactTeam(
  data: InsertContactTeam
): Promise<ContactsApiResult<ContactTeam>> {
  try {
    const apiUrl = buildProductApiUrl('/api/contacts/teams');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to create team (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error creating team:', error);
    return { success: false, error: error.message };
  }
}

export async function updateContactTeam(
  id: string,
  data: Partial<InsertContactTeam>
): Promise<ContactsApiResult<ContactTeam>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/teams/${id}`);
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to update team (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error updating team:', error);
    return { success: false, error: error.message };
  }
}

export async function deleteContactTeam(
  id: string
): Promise<ContactsApiResult<{ success: boolean }>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/teams/${id}`);
    const response = await fetch(apiUrl, { method: 'DELETE' });
    
    if (!response.ok) {
      return { success: false, error: `Failed to delete team (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error deleting team:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Contacts
// ============================================================================

export interface GetContactsOptions {
  productId?: string;
  organizationId?: string;
  teamId?: string;
  search?: string;
  withDetails?: boolean;
}

export async function getContacts(
  options: GetContactsOptions = {}
): Promise<ContactsApiResult<Contact[] | ContactWithDetails[]>> {
  try {
    const params: Record<string, string> = {};
    if (options.productId) params.productId = options.productId;
    if (options.organizationId) params.organizationId = options.organizationId;
    if (options.teamId) params.teamId = options.teamId;
    if (options.search) params.search = options.search;
    if (options.withDetails) params.withDetails = 'true';
    
    const apiUrl = buildProductApiUrl('/api/contacts', params);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch contacts (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching contacts:', error);
    return { success: false, error: error.message };
  }
}

export async function getContact(
  id: string,
  withDetails?: boolean
): Promise<ContactsApiResult<Contact | ContactWithDetails>> {
  try {
    const params: Record<string, string> = {};
    if (withDetails) params.withDetails = 'true';
    
    const apiUrl = buildProductApiUrl(`/api/contacts/${id}`, params);
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      return { success: false, error: `Failed to fetch contact (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error fetching contact:', error);
    return { success: false, error: error.message };
  }
}

export async function createContact(
  data: InsertContact
): Promise<ContactsApiResult<Contact>> {
  try {
    const apiUrl = buildProductApiUrl('/api/contacts');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to create contact (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error creating contact:', error);
    return { success: false, error: error.message };
  }
}

export async function updateContact(
  id: string,
  data: Partial<InsertContact>
): Promise<ContactsApiResult<Contact>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/${id}`);
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to update contact (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error updating contact:', error);
    return { success: false, error: error.message };
  }
}

export async function deleteContact(
  id: string
): Promise<ContactsApiResult<{ success: boolean }>> {
  try {
    const apiUrl = buildProductApiUrl(`/api/contacts/${id}`);
    const response = await fetch(apiUrl, { method: 'DELETE' });
    
    if (!response.ok) {
      return { success: false, error: `Failed to delete contact (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error deleting contact:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Import Operations
// ============================================================================

export interface CsvImportData {
  productId: string;
  organizationId?: string;
  contacts: Array<{
    name: string;
    email?: string;
    phone?: string;
    role?: string;
    notes?: string;
  }>;
}

export async function importContactsFromCsv(
  data: CsvImportData
): Promise<ContactsApiResult<{ imported: number; contacts: Contact[] }>> {
  try {
    const apiUrl = buildProductApiUrl('/api/contacts/import/csv');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to import contacts (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error importing contacts from CSV:', error);
    return { success: false, error: error.message };
  }
}

export interface GoogleImportData {
  productId: string;
  organizationId?: string;
  accessToken: string;
}

export async function importContactsFromGoogle(
  data: GoogleImportData
): Promise<ContactsApiResult<{ imported: number; contacts: Contact[] }>> {
  try {
    const apiUrl = buildProductApiUrl('/api/contacts/import/google');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      return { success: false, error: `Failed to import contacts from Google (${response.status})` };
    }
    
    const json = await response.json();
    return { success: true, data: json.data };
  } catch (error: any) {
    console.error('[Contacts API] Error importing contacts from Google:', error);
    return { success: false, error: error.message };
  }
}
